import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProfessionalLayout from '../../components/layout/ProfessionalLayout';
import { employeeAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, TrendingUp, Copy, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import ScoreExplainerModal from '../../components/common/ScoreExplainerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [activity, setActivity] = useState([]);
  const [profile, setProfile] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [showScoreExplainer, setShowScoreExplainer] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  
  // Score animation
  const [displayScore, setDisplayScore] = useState(0);
  const scoreAnimated = useRef(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Animate score
  useEffect(() => {
    if (scoreAnimated.current || !tierData?.score) return;
    scoreAnimated.current = true;
    
    const targetScore = tierData.score;
    const duration = 1500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(targetScore * eased));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    setTimeout(() => requestAnimationFrame(animate), 300);
  }, [tierData]);

  const fetchDashboardData = async () => {
    try {
      // Check onboarding first
      const profileRes = await employeeAPI.getProfile();
      if (!profileRes.data.onboarding_complete) {
        navigate('/professional/onboarding', { replace: true });
        return;
      }
      setProfile(profileRes.data);
      
      // Fetch remaining data in parallel
      const [statsRes, recordsRes, activityRes, tierRes] = await Promise.all([
        employeeAPI.getDashboardStats().catch(() => ({ data: {} })),
        employeeAPI.getRecords().catch(() => ({ data: [] })),
        employeeAPI.getActivity().catch(() => ({ data: [] })),
        employeeAPI.getVettTier().catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setRecords(recordsRes.data?.slice(0, 4) || []);
      setActivity(activityRes.data?.slice(0, 5) || []);
      setTierData(tierRes.data);
      
      // Fetch referral data
      try {
        const userId = user?.id || profileRes.data?.user_id;
        const referralRes = await fetch(`${API_URL}/api/promo/referral/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId })
        });
        if (referralRes.ok) {
          const data = await referralRes.json();
          setReferralData(data);
        }
      } catch (err) {
        console.error('Error fetching referral data:', err);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    if (!referralData?.referral_link) return;
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setReferralCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      'NONE': 'var(--pro-light-text-faint)',
      'BASIC': '#6B7280',
      'VERIFIED': '#6C97FF',
      'ESTABLISHED': '#8B7CFF',
      'DISTINGUISHED': '#D8B45C'
    };
    return colors[tier] || colors.NONE;
  };

  const getTierClass = (tier) => {
    const classes = {
      'NONE': 'pro-tier-unverified',
      'BASIC': 'pro-tier-unverified',
      'VERIFIED': 'pro-tier-verified',
      'ESTABLISHED': 'pro-tier-established',
      'DISTINGUISHED': 'pro-tier-distinguished'
    };
    return classes[tier] || classes.NONE;
  };

  const scoreOffset = 314.16 - ((displayScore || 0) / 100) * 314.16;

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F8EF7]" />
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      {/* Header */}
      <div className="pro-header-row">
        <div>
          <div className="pro-eyebrow">YOUR DASHBOARD</div>
          <h1 className="pro-page-title">Welcome back, {profile?.name?.split(' ')[0] || 'there'}.</h1>
          <p className="pro-page-sub">
            Here's where your VETT Score stands and what's moving across your verified records.
          </p>
        </div>
        <Link to="/professional/request-record" className="pro-btn pro-btn-primary" data-testid="request-record-btn">
          Request a new record →
        </Link>
      </div>

      {/* Stats Row */}
      <div className="pro-stat-row">
        {/* Score Card */}
        <div className="pro-card" style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          <div className="pro-score-ring-wrap">
            <svg viewBox="0 0 120 120">
              <circle className="pro-score-ring-bg" cx="60" cy="60" r="50" />
              <circle 
                className="pro-score-ring-fill" 
                cx="60" 
                cy="60" 
                r="50"
                style={{ 
                  strokeDashoffset: scoreOffset,
                  stroke: getTierColor(tierData?.tier)
                }}
              />
            </svg>
            <div className="pro-score-ring-label">
              <div className="pro-score-ring-num">{displayScore}</div>
              <div className="pro-score-ring-max">/ 100</div>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <div style={{ fontSize: '13px', color: 'var(--pro-light-text-muted)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              VETT Score
              <button 
                onClick={() => setShowScoreExplainer(true)}
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'rgba(79,142,247,0.15)',
                  border: '1px solid rgba(79,142,247,0.3)',
                  color: '#4F8EF7',
                  fontSize: '11px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                data-testid="dashboard-score-help"
              >
                ?
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <span className={`pro-tier-pill ${getTierClass(tierData?.tier)}`}>
                {tierData?.tier_name || 'UNVERIFIED'}
              </span>
              {tierData?.score_change > 0 && (
                <span style={{ fontSize: '12.5px', color: 'var(--pro-light-text-faint)' }}>
                  up from {tierData.previous_score} after your last co-sign
                </span>
              )}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--pro-light-text-faint)', lineHeight: '1.6' }}>
              Unverified → Verified → <strong style={{ color: 'var(--pro-light-text)' }}>Established</strong> → Distinguished. 
              Two more strong co-signed records typically moves you into Distinguished.
            </div>
          </div>
        </div>

        {/* Verified Records */}
        <div className="pro-stat-tile">
          <div className="pro-stat-tile-label">Verified Records</div>
          <div className="pro-stat-tile-val">{stats?.verified_records || 0}</div>
          <div className="pro-stat-tile-sub">Published and locked to your VETT ID</div>
        </div>

        {/* Pending */}
        <div className="pro-stat-tile">
          <div className="pro-stat-tile-label">Pending Verifications</div>
          <div className="pro-stat-tile-val">{stats?.pending_records || 0}</div>
          <div className="pro-stat-tile-sub">Awaiting HR or manager co-sign</div>
        </div>

        {/* Views */}
        <div className="pro-stat-tile">
          <div className="pro-stat-tile-label">VETT ID Views</div>
          <div className="pro-stat-tile-val">{stats?.profile_views || 0}</div>
          <div className="pro-stat-tile-sub">Profile lookups, all time</div>
        </div>
      </div>

      {/* Mid Grid: Records + Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Recent Records */}
        <div className="pro-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <h2 className="pro-card-title">Recent Records</h2>
            <span style={{ fontSize: '12.5px', color: 'var(--pro-light-text-faint)' }}>
              {records.length} total
            </span>
          </div>

          {records.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--pro-light-text-faint)' }}>
              No records yet. Request your first verification to get started.
            </div>
          ) : (
            records.map((record) => (
              <Link 
                key={record.id} 
                to={`/professional/records/${record.id}`} 
                className="pro-record-row"
                data-testid={`record-row-${record.id}`}
              >
                <div>
                  <div className="pro-record-row-company">{record.company_name || record.company}</div>
                  <div className="pro-record-row-role">{record.job_title || record.role}</div>
                  <div className="pro-record-row-dates">
                    {record.start_date?.slice(0, 7)} – {record.end_date?.slice(0, 7) || 'current'}
                  </div>
                </div>
                <div className="pro-record-row-right">
                  {record.performance_rating ? (
                    <span className="pro-record-row-stars">
                      {'★'.repeat(record.performance_rating)}{'☆'.repeat(5 - record.performance_rating)}
                    </span>
                  ) : (
                    <span className="pro-record-row-stars no-rating">AWAITING RATING</span>
                  )}
                  <span className={`pro-status-badge pro-status-${(record.status || 'draft').toLowerCase()}`}>
                    {record.status || 'Draft'}
                  </span>
                </div>
              </Link>
            ))
          )}
        </div>

        {/* Recent Activity */}
        <div className="pro-card">
          <h2 className="pro-card-title" style={{ marginBottom: '16px' }}>Recent Activity</h2>
          {activity.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--pro-light-text-faint)' }}>
              No activity yet.
            </div>
          ) : (
            activity.map((item, index) => (
              <div key={index} className="pro-activity-item">
                <div className={`pro-activity-icon ${item.type === 'view' ? 'signup' : 'record'}`}>
                  {item.type === 'view' ? '👁' : item.type === 'cosign' ? '✓' : '📧'}
                </div>
                <div>
                  <div className="pro-activity-text">{item.message || item.description}</div>
                  <div className="pro-activity-time">
                    {new Date(item.created_at || item.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Nudge Card */}
      {tierData && tierData.next_tier_requirements?.length > 0 && (
        <div className="pro-nudge-card">
          <div className="pro-nudge-card-icon">
            <TrendingUp className="w-5 h-5 text-[#4F8EF7]" />
          </div>
          <div>
            <div className="pro-nudge-card-title">Improve your score</div>
            <div className="pro-nudge-card-desc">
              {tierData.next_tier_requirements[0]}{' '}
              <Link to="/professional/vett-id" style={{ color: 'var(--pro-vett-blue)', fontWeight: '600', textDecoration: 'none' }}>
                See your full score breakdown →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Referral Card */}
      {referralData && (
        <div className="pro-card" style={{ marginBottom: '24px' }} data-testid="referral-card">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '240px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <Share2 className="w-5 h-5 text-[#4F8EF7]" />
                <span className="pro-card-title" style={{ marginBottom: 0 }}>Share VETT Pro</span>
              </div>
              <p style={{ fontSize: '13.5px', color: 'var(--pro-light-text-muted)', lineHeight: '1.6', marginBottom: '16px' }}>
                Invite friends and colleagues to VETT Pro. When they sign up, you both earn extra months of Early Access Pro.
              </p>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                padding: '12px 16px', 
                background: 'var(--pro-light-bg)', 
                borderRadius: '10px',
                border: '1px solid var(--pro-light-border)'
              }}>
                <span style={{ 
                  fontFamily: 'var(--pro-font-mono)', 
                  fontSize: '13px', 
                  color: 'var(--pro-vett-blue)',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }} data-testid="referral-link">
                  {referralData.referral_link}
                </span>
                <button
                  onClick={copyReferralLink}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    background: referralCopied ? 'var(--pro-vett-teal)' : 'var(--pro-vett-blue)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  data-testid="copy-referral-btn"
                >
                  {referralCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {referralCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            {(referralData.total_referrals > 0 || referralData.early_access_credits > 0) && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px',
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(0,201,167,0.08), rgba(79,142,247,0.05))',
                borderRadius: '12px',
                border: '1px solid rgba(0,201,167,0.2)',
                minWidth: '140px'
              }}>
                <div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--pro-light-text)', fontFamily: 'var(--pro-font-mono)' }}>
                    {referralData.total_referrals || 0}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--pro-light-text-muted)' }}>Referrals</div>
                </div>
                <div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--pro-vett-teal)', fontFamily: 'var(--pro-font-mono)' }}>
                    +{referralData.early_access_credits || 0}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--pro-light-text-muted)' }}>Bonus months</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA Strip */}
      <div className="pro-cta-strip">
        <div>
          <div className="pro-cta-strip-title">Add another employer to your history.</div>
          <div className="pro-cta-strip-desc">
            Every co-signed record raises your VETT Score and strengthens your Passport.
          </div>
        </div>
        <Link to="/professional/request-record" className="pro-btn pro-btn-primary">
          Request a new record →
        </Link>
      </div>

      {/* Score Explainer Modal */}
      <ScoreExplainerModal 
        isOpen={showScoreExplainer} 
        onClose={() => setShowScoreExplainer(false)} 
      />
    </ProfessionalLayout>
  );
};

export default ProfessionalDashboard;
